import { User } from '@/types/user';
import * as amplitude from '@amplitude/analytics-browser';
import { isValidPhoneNumber } from 'libphonenumber-js';
import Cookies from 'universal-cookie';
import { getMainFbPixel, initTrackerFacebook, removeCookie, setCookie, trackMpUserEvent } from '.';
import { getEmbedContext } from '@/helpers/embed';
import { isServer } from './general';
import { EXTRA_TIME_REQUIREMENT } from '@/types/state/book';
const cookies = new Cookies();

/**
 * Framed (booking-widget iframe) the SPA holds a widget-own session under its
 * own key and never reads or writes the site's token key / cookie — so the
 * widget can neither inherit nor overwrite the bokadirekt.se login. Partitioned
 * or blocked storage falls back to memory for the page's lifetime.
 */
export const EMBED_SESSION_KEY = 'bd-embed:uSessionID';
let memoryToken: string | null = null;

const isFramed = () => getEmbedContext() === 'framed';

function readEmbedToken(): string | null {
  try {
    return localStorage.getItem(EMBED_SESSION_KEY);
  } catch {
    return null;
  }
}

export function loggedUser() {
  // return authorization header with jwt token
  let token = null;
  if (!isServer) {
    if (isFramed()) {
      return memoryToken ?? readEmbedToken();
    }
    token = cookies.get('BokadirektuSessionID');
    if (!token) {
      try {
        token = localStorage.getItem('uSessionID');
      } catch (e) {
        // ignore localStorage access errors and leave token as null
      }
    }
  }

  return token;
}

export function storeUser(userData) {
  if (!isServer && isFramed()) {
    memoryToken = userData.token;
    try {
      localStorage.setItem(EMBED_SESSION_KEY, userData.token);
    } catch {
      // partitioned / blocked storage — memory only
    }
    trackMpUserEvent(userData);
    return userData;
  }

  // Save to storage
  localStorage.setItem('uSessionID', userData.token);
  setCookie('BokadirektuSessionID', userData.token);
  trackMpUserEvent(userData);

  // don't save "user" anymore in localstorage - cleanup: remove after 1.Aug.2022
  localStorage.removeItem('user');

  return userData;
}

export function removeUser() {
  if (isServer) {
    return;
  }

  if (isFramed()) {
    // The site keys and cookies below belong to the top-level bokadirekt.se user.
    memoryToken = null;
    try {
      localStorage.removeItem(EMBED_SESSION_KEY);
    } catch {
      // ignore
    }
  } else {
    localStorage.removeItem('user');
    localStorage.removeItem('uSessionID');
    localStorage.removeItem('uFilterActive');
    localStorage.removeItem('uFilterCanceled');
    localStorage.removeItem('mpLastVisited');
    localStorage.removeItem('mpLastVisitedPlaces');
    localStorage.removeItem('mpPlaceVisits');
    localStorage.removeItem('mpClientPlaceBookings');
    removeCookie('clientBooker');
    removeCookie('BokadirektuSessionID');
    //should also delete cookie BokadirektCookiePreferencesMP ??
  }
  trackMpUserEvent();

  amplitude.reset();

  if (window._cio) {
    window._cio.reset();
  } else {
    setTimeout(() => {
      if (window._cio) {
        window._cio.reset();
      }
    }, 200);
  }

  if (getMainFbPixel()) {
    initTrackerFacebook(getMainFbPixel(), { em: null, ph: null });
  }
}

export const getUserInitials = (user: User) => {
  const { about } = user;
  const { givenName, familyName } = about;
  return givenName && familyName ? `${givenName.charAt(0).toUpperCase()}${familyName.charAt(0).toUpperCase()}` : '';
};

export const validatePhoneNumber = (phoneNumber: string) => {
  let swedishNumberStartingWith00 = /^0046\s*(7[02369])\s*(\d{4})\s*(\d{3})$/;
  let internationalNumers = /^\+(?!46)[0-9\s]+$/;
  return (
    isValidPhoneNumber(phoneNumber, 'SE') ||
    swedishNumberStartingWith00.test(phoneNumber) ||
    internationalNumers.test(phoneNumber)
  );
};

export const isPlaceFavorite = (user: any, placeId: number) => {
  return user?.favorites?.indexOf?.(placeId) !== -1;
};

export const getExtraTimeRequirementForUser = (user: any, placeId: number | undefined) => {
  if (!placeId) {
    return EXTRA_TIME_REQUIREMENT.NotAnsweredYet;
  }
  return user?.about?.extraTimeRequirement?.[placeId] || EXTRA_TIME_REQUIREMENT.NotAnsweredYet;
};

export const showExtraTimeRequiredForUser = (user: any, placeId: number | undefined) => {
  return getExtraTimeRequirementForUser(user, placeId) === EXTRA_TIME_REQUIREMENT.NotAnsweredYet;
};
